import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, Users, FileText, AlertTriangle, BookOpen, Activity } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

export function DashboardPage() {
  const { user } = useAuthStore();

  const stats = [
    {
      title: "Compliance Score",
      value: "85%",
      description: "NIS2 compliance rating",
      icon: Shield,
      color: "text-green-600"
    },
    {
      title: "Active Users",
      value: "12",
      description: "Registered users",
      icon: Users,
      color: "text-blue-600"
    },
    {
      title: "Documents",
      value: "45",
      description: "ISMS documents",
      icon: FileText,
      color: "text-purple-600"
    },
    {
      title: "Open Incidents",
      value: "3",
      description: "Security incidents",
      icon: AlertTriangle,
      color: "text-red-600"
    },
    {
      title: "Training Sessions",
      value: "8",
      description: "Completed this month",
      icon: BookOpen,
      color: "text-orange-600"
    },
    {
      title: "Assets",
      value: "127",
      description: "Information assets",
      icon: Activity,
      color: "text-teal-600"
    }
  ];

  const recentActivities = [
    {
      action: "Document updated",
      item: "Information Security Policy v2.1",
      time: "2 hours ago",
      user: "John Doe"
    },
    {
      action: "New incident reported",
      item: "Phishing attempt detected",
      time: "4 hours ago",
      user: "Security Team"
    },
    {
      action: "Training completed",
      item: "Cybersecurity Awareness",
      time: "1 day ago",
      user: "Jane Smith"
    },
    {
      action: "Asset registered",
      item: "New server infrastructure",
      time: "2 days ago",
      user: "IT Department"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6">
        <h1 className="text-3xl font-bold mb-2">
          Welcome back, {user?.firstName} {user?.lastName}
        </h1>
        <p className="text-blue-100">
          Your ISMS dashboard provides an overview of your organization's security posture and NIS2 compliance status.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <IconComponent className={`h-4 w-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">
                  {stat.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>
              Common tasks and operations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full justify-start" variant="outline">
              <FileText className="mr-2 h-4 w-4" />
              Create New Document
            </Button>
            <Button className="w-full justify-start" variant="outline">
              <AlertTriangle className="mr-2 h-4 w-4" />
              Report Incident
            </Button>
            <Button className="w-full justify-start" variant="outline">
              <Activity className="mr-2 h-4 w-4" />
              Register Asset
            </Button>
            <Button className="w-full justify-start" variant="outline">
              <BookOpen className="mr-2 h-4 w-4" />
              Schedule Training
            </Button>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>
              Latest updates and changes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.map((activity, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {activity.action}
                    </p>
                    <p className="text-sm text-gray-500">
                      {activity.item}
                    </p>
                    <p className="text-xs text-gray-400">
                      {activity.time} • {activity.user}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Overview */}
      <Card>
        <CardHeader>
          <CardTitle>NIS2 Compliance Overview</CardTitle>
          <CardDescription>
            Your organization's compliance status across different domains
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { domain: "Risk Management", progress: 90, status: "Excellent" },
              { domain: "Incident Response", progress: 85, status: "Good" },
              { domain: "Business Continuity", progress: 75, status: "Good" },
              { domain: "Supply Chain Security", progress: 60, status: "Needs Improvement" },
              { domain: "Cybersecurity Measures", progress: 80, status: "Good" },
            ].map((item, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{item.domain}</span>
                  <span className={`text-sm ${
                    item.progress >= 80 ? 'text-green-600' : 
                    item.progress >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {item.status}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      item.progress >= 80 ? 'bg-green-500' : 
                      item.progress >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${item.progress}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
